import { integrationNameAtom } from '@/atoms/integrationAuthAtoms';
import IntegrationTierAccordion from '@/components/modal/heidi-pro-modal/integration-tier-accordion';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useIsUserRegionSameAsKindeRegion } from '@/hooks/useIsUserRegionSameAsKindeRegion';
import { cn } from '@/lib/cn';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { Period } from '@heidi/commerce/contracts';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { TypographyH4, TypographyP } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

type Props = {
  open: boolean;
  shouldBlurOverlay?: boolean;
  hideOverlay?: boolean;
  onDismiss?: () => void;
  tierToUpgrade: SubscriptionTier.PRO | SubscriptionTier.PRO_PLUS;
};

export function IntegrationUpgradeModal({
  open,
  shouldBlurOverlay,
  hideOverlay,
  onDismiss,
  tierToUpgrade,
}: Props) {
  const { t } = useTranslations();
  const { userTier } = useQueryUserTier();
  const integrationName = useAtomValue(integrationNameAtom);
  const { data: userFreeTrialHistory } = useQueryUser((data) => ({
    free_trial_history_tiers: data.free_trial_history_tiers,
  }));
  const { userRegionSameAsKindeRegion } = useIsUserRegionSameAsKindeRegion();
  const hasUsedProPlusFreeTrial = useMemo(
    () =>
      userFreeTrialHistory?.free_trial_history_tiers?.find(
        (tier) => tier === SubscriptionTier.PRO_PLUS
      ),
    [userFreeTrialHistory]
  );

  if (isAppleCompliantBuild()) return null;

  return (
    <DialogDrawer modal dismissible={false} open={open}>
      <DialogDrawerContent
        disableDrag
        hideOverlay={hideOverlay}
        onCloseIconClick={onDismiss}
        className={cn(
          'flex max-h-[90dvh] flex-col overflow-hidden border-none p-6 md:max-w-[500px]',
          !hideOverlay && 'z-[1000]'
        )}
        overlayClassName={shouldBlurOverlay ? 'backdrop-blur-[3px]' : ''}
      >
        <DialogDrawerTitle className="hidden">
          {t('upgradeModal.upgradeToUseIntegration')}
        </DialogDrawerTitle>
        <div>
          <TypographyH4 className="mb-1">
            {tierToUpgrade === SubscriptionTier.PRO_PLUS && (
              <>
                {hasUsedProPlusFreeTrial
                  ? t('upgradeModal.upgradeToPractice')
                  : t('upgradeModal.tryHeidiPractice')}
              </>
            )}
            {tierToUpgrade === SubscriptionTier.PRO && (
              <>
                {/* Note: to get to this code, the user must have already used their PRO reverse trial */}
                {t('upgradeModal.upgradeToPro')}
              </>
            )}
          </TypographyH4>

          {userRegionSameAsKindeRegion && (
            <TypographyP className="text-text-secondary">
              {tierToUpgrade === SubscriptionTier.PRO_PLUS &&
                t('upgradeModal.integrationRequiresPracticeTier')}
              {tierToUpgrade === SubscriptionTier.PRO &&
                t('upgradeModal.integrationRequiresProTier')}{' '}
              {integrationName || t('upgradeModal.yourIntegration')}.
            </TypographyP>
          )}
        </div>
        <IntegrationTierAccordion
          userTier={userTier}
          period={Period.ONE_YEAR}
          tierToUpgrade={tierToUpgrade}
          onDismiss={onDismiss}
        />
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
